import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronLeft, MapPin, Video, Filter, Search, Languages, Clock, CheckCircle2 } from "lucide-react";
import api, { drName } from "../lib/api";
import { FullScreenLoader, EmptyState } from "../components/ui-kit";

const todayISO = () => new Date().toISOString().slice(0, 10);

const BookAppointment = () => {
  const nav = useNavigate();
  const [filters, setFilters] = useState({ city: "", specialty: "", language: "", mode: "", q: "" });
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDoctor, setOpenDoctor] = useState(null); // full doctor obj
  const [date, setDate] = useState(todayISO());
  const [slots, setSlots] = useState([]);
  const [slot, setSlot] = useState("");
  const [mode, setMode] = useState("Remote");
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [notes, setNotes] = useState("");
  const [success, setSuccess] = useState(null);

  const search = async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await api.get("/public/doctors", { params });
      setDoctors(data || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { search(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openBooking = async (doc) => {
    setOpenDoctor(doc);
    setDate(todayISO()); setSlot(""); setSlots([]);
    setMode(filters.mode || "Remote");
    loadSlots(doc.id, todayISO());
  };
  const loadSlots = async (docId, d) => {
    setSlotsLoading(true);
    try {
      const { data } = await api.get(`/public/doctors/${docId}/slots`, { params: { date: d } });
      setSlots(data?.slots || []);
    } finally { setSlotsLoading(false); }
  };

  const changeDate = (d) => { setDate(d); setSlot(""); if (openDoctor) loadSlots(openDoctor.id, d); };

  const confirmBooking = async () => {
    if (!openDoctor || !slot) return;
    setConfirming(true);
    try {
      const { data } = await api.post("/patient/appointments", {
        doctor_id: openDoctor.id, date, time: slot, mode, notes,
      });
      setSuccess(data);
    } catch (e) {
      alert(e?.response?.data?.detail || "Booking failed");
    } finally { setConfirming(false); }
  };

  const dateOptions = useMemo(() => {
    const arr = [];
    const now = new Date();
    for (let i = 0; i < 14; i++) {
      const d = new Date(now.getTime() + i * 86400000);
      arr.push({
        iso: d.toISOString().slice(0, 10),
        day: d.toLocaleDateString(undefined, { weekday: "short" }),
        num: d.getDate(),
      });
    }
    return arr;
  }, []);

  if (success) {
    return (
      <div className="min-h-[100dvh] p-6 pt-12 flex flex-col items-center text-center">
        <div className="w-20 h-20 rounded-full bg-[#E6F0EC] flex items-center justify-center mb-6">
          <CheckCircle2 size={38} className="text-[#0A4C36]" />
        </div>
        <h2 className="font-instrument text-2xl font-semibold text-[#0A4C36] mb-1">Booking confirmed</h2>
        <p className="text-sm text-[#57534E] mb-6">Your appointment is on the doctor&rsquo;s chart.</p>
        <div className="card w-full text-left mb-4">
          <div className="text-xs text-[#78716C]">Doctor</div>
          <div className="font-medium text-[#1C1917] mb-2">{drName(success.doctor_name)}</div>
          <div className="text-xs text-[#78716C]">Date & time</div>
          <div className="font-medium text-[#1C1917] mb-2">{success.date} · {success.time}</div>
          <div className="text-xs text-[#78716C]">Mode</div>
          <div className="font-medium text-[#1C1917]">{success.mode}</div>
        </div>
        {success.mode === "Remote" && success.meeting_url && (
          <Link to={`/meeting?url=${encodeURIComponent(success.meeting_url)}`} data-testid="success-join" className="btn-primary text-center no-underline mb-3">Join meeting now</Link>
        )}
        <Link to="/dashboard" data-testid="success-home" className="btn-secondary text-center no-underline">Back to dashboard</Link>
      </div>
    );
  }

  if (openDoctor) {
    return (
      <div className="min-h-[100dvh] pb-32">
        <div className="px-5 pt-6 pb-3 flex items-center gap-3">
          <button data-testid="booking-back" onClick={() => setOpenDoctor(null)} className="w-9 h-9 rounded-full bg-white flex items-center justify-center border border-[#0A4C36]/10">
            <ChevronLeft size={20} className="text-[#0A4C36]" />
          </button>
          <h1 className="font-instrument text-2xl font-semibold text-[#0A4C36]">Book slot</h1>
        </div>
        <div className="px-5">
          <div className="card">
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 rounded-2xl bg-[#0A4C36] text-white flex items-center justify-center font-instrument text-xl font-semibold">
                {(openDoctor.name || "D").slice(0, 1).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-instrument text-lg font-semibold text-[#1C1917]">{drName(openDoctor.name)}</div>
                <div className="text-xs text-[#78716C]">{(openDoctor.specialties || []).join(", ") || openDoctor.qualification || ""}</div>
                <div className="flex items-center gap-2 text-xs mt-1">
                  {openDoctor.languages?.length > 0 && <span className="text-[#57534E] flex items-center gap-1"><Languages size={12} />{openDoctor.languages.join(", ")}</span>}
                </div>
              </div>
            </div>
          </div>

          <h3 className="font-instrument text-lg font-semibold mt-6 mb-3 text-[#1C1917]">Consultation mode</h3>
          <div className="grid grid-cols-2 gap-3">
            <button
              data-testid="mode-remote"
              onClick={() => setMode("Remote")}
              className={`p-4 rounded-2xl text-left border transition-all ${
                mode === "Remote"
                  ? "bg-[#0A4C36] text-white border-[#0A4C36] shadow-md shadow-[#0A4C36]/20"
                  : "bg-white border-[#0A4C36]/10 text-[#1C1917]"
              }`}
            >
              <Video size={18} className={mode === "Remote" ? "text-white" : "text-[#0A4C36]"} />
              <div className="font-instrument font-semibold mt-1.5">Remote</div>
              <div className={`text-xs mt-0.5 ${mode === "Remote" ? "text-white/80" : "text-[#78716C]"}`}>
                Video call · Daily.co link
              </div>
            </button>
            <button
              data-testid="mode-in-person"
              onClick={() => setMode("In-person")}
              className={`p-4 rounded-2xl text-left border transition-all ${
                mode === "In-person"
                  ? "bg-[#F4844B] text-white border-[#F4844B] shadow-md shadow-[#F4844B]/25"
                  : "bg-white border-[#0A4C36]/10 text-[#1C1917]"
              }`}
            >
              <MapPin size={18} className={mode === "In-person" ? "text-white" : "text-[#F4844B]"} />
              <div className="font-instrument font-semibold mt-1.5">In-person</div>
              <div className={`text-xs mt-0.5 ${mode === "In-person" ? "text-white/80" : "text-[#78716C]"}`}>
                Visit the clinic
              </div>
            </button>
          </div>

          <h3 className="font-instrument text-lg font-semibold mt-6 mb-3 text-[#1C1917]">Choose date</h3>
          <div className="flex gap-2 overflow-x-auto hide-scrollbar -mx-5 px-5 pb-1">
            {dateOptions.map((d) => (
              <button
                key={d.iso}
                data-testid={`date-${d.iso}`}
                onClick={() => changeDate(d.iso)}
                className={`min-w-[60px] py-3 rounded-2xl text-center transition-all ${
                  date === d.iso ? "bg-[#0A4C36] text-white" : "bg-white border border-[#0A4C36]/10 text-[#57534E]"
                }`}
              >
                <div className="text-[10px] uppercase tracking-wide">{d.day}</div>
                <div className="font-instrument text-xl font-semibold">{d.num}</div>
              </button>
            ))}
          </div>

          <h3 className="font-instrument text-lg font-semibold mt-6 mb-3 text-[#1C1917]">Available slots</h3>
          {slotsLoading ? (
            <div className="text-center py-8 text-sm text-[#78716C]">Loading slots…</div>
          ) : slots.length === 0 ? (
            <div className="text-center py-8 text-sm text-[#78716C]">No slots on this date</div>
          ) : (
            <div className="grid grid-cols-3 gap-2.5">
              {slots.map((s) => (
                <button
                  key={s}
                  data-testid={`slot-${s}`}
                  onClick={() => setSlot(s)}
                  className={`py-3 rounded-2xl text-sm font-medium transition-all ${
                    slot === s
                      ? "bg-[#F4844B] text-white border border-[#F4844B] shadow-md shadow-[#F4844B]/25"
                      : "bg-white border border-[#0A4C36]/10 text-[#1C1917] active:bg-[#FCD9C8]"
                  }`}
                >{s}</button>
              ))}
            </div>
          )}

          {slot && (
            <div className="mt-6">
              <label className="block">
                <span className="text-xs font-medium text-[#57534E] ml-1">Notes for doctor (optional)</span>
                <textarea data-testid="booking-notes" rows="3" className="input-field mt-1 resize-none" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Symptoms, concerns, or context…" />
              </label>

              <div className="mt-5 p-4 rounded-2xl bg-[#E6F0EC] border border-[#0A4C36]/10">
                <div className="text-xs text-[#57534E] mb-2 font-medium uppercase tracking-wide">Booking summary</div>
                <div className="flex items-start justify-between gap-3 mb-1">
                  <span className="text-sm text-[#78716C]">Doctor</span>
                  <span className="text-sm font-medium text-[#0A4C36] text-right">{drName(openDoctor.name)}</span>
                </div>
                <div className="flex items-start justify-between gap-3 mb-1">
                  <span className="text-sm text-[#78716C]">Date &amp; time</span>
                  <span className="text-sm font-medium text-[#0A4C36]">{date} · {slot}</span>
                </div>
                <div className="flex items-start justify-between gap-3">
                  <span className="text-sm text-[#78716C]">Mode</span>
                  <span className="text-sm font-medium text-[#0A4C36] flex items-center gap-1">
                    {mode === "Remote" ? <Video size={14} /> : <MapPin size={14} />} {mode}
                  </span>
                </div>
              </div>

              <button
                data-testid="confirm-booking"
                disabled={confirming}
                onClick={confirmBooking}
                className="btn-primary mt-4"
              >
                {confirming ? "Booking…" : `Book ${mode} appointment`}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="pb-24">
      <div className="px-5 pt-6 pb-4 flex items-center gap-3">
        <button data-testid="book-back" onClick={() => nav(-1)} className="w-9 h-9 rounded-full bg-white flex items-center justify-center border border-[#0A4C36]/10">
          <ChevronLeft size={20} className="text-[#0A4C36]" />
        </button>
        <div>
          <h1 className="font-instrument text-2xl font-semibold text-[#0A4C36] leading-tight">Book appointment</h1>
          <p className="text-xs text-[#78716C]">Find a doctor for you</p>
        </div>
      </div>

      {/* Search */}
      <div className="px-5">
        <div className="relative">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A8A29E]" />
          <input
            data-testid="search-doctors"
            className="input-field pl-11" placeholder="Search doctor or specialty…"
            value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            onKeyDown={(e) => e.key === "Enter" && search()}
          />
        </div>
      </div>

      {/* Filters */}
      <div className="px-5 mt-3 grid grid-cols-2 gap-2.5">
        <input data-testid="filter-city" className="input-field !py-3" placeholder="City" value={filters.city} onChange={(e) => setFilters({ ...filters, city: e.target.value })} />
        <input data-testid="filter-specialty" className="input-field !py-3" placeholder="Specialty" value={filters.specialty} onChange={(e) => setFilters({ ...filters, specialty: e.target.value })} />
        <input data-testid="filter-language" className="input-field !py-3" placeholder="Language" value={filters.language} onChange={(e) => setFilters({ ...filters, language: e.target.value })} />
        <select data-testid="filter-mode" className="input-field !py-3" value={filters.mode} onChange={(e) => setFilters({ ...filters, mode: e.target.value })}>
          <option value="">All modes</option>
          <option value="Remote">Remote</option>
          <option value="In-person">In-person</option>
        </select>
      </div>
      <div className="px-5 mt-3">
        <button data-testid="apply-filters" onClick={search} className="btn-primary flex items-center justify-center gap-2 text-sm py-3">
          <Filter size={16} /> Apply filters
        </button>
      </div>

      {/* Doctor list */}
      <div className="px-5 mt-5 space-y-3">
        {loading ? <FullScreenLoader /> : doctors.length === 0 ? (
          <EmptyState title="No doctors found" subtitle="Try clearing your filters" />
        ) : doctors.map((d) => (
          <button
            key={d.id}
            data-testid={`doctor-card-${d.id}`}
            onClick={() => openBooking(d)}
            className="w-full card text-left"
          >
            <div className="flex items-start gap-3">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#0A4C36] to-[#0e6b4c] text-white flex items-center justify-center font-instrument text-xl font-semibold flex-shrink-0">
                {(d.name || "D").slice(0, 1).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-instrument text-lg font-semibold text-[#1C1917]">{drName(d.name)}</div>
                <div className="text-xs text-[#78716C]">{(d.specialties || []).join(", ") || d.qualification || "General practice"}</div>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  {d.address && <span className="text-xs text-[#57534E] flex items-center gap-1"><MapPin size={12} />{d.address}</span>}
                  {d.clinic_name && <span className="text-xs text-[#57534E] flex items-center gap-1"><Clock size={12} />{d.clinic_name}</span>}
                </div>
                {d.languages?.length > 0 && (
                  <div className="flex gap-1.5 mt-2 flex-wrap items-center">
                    <Languages size={12} className="text-[#0A4C36]" />
                    {d.languages.slice(0, 3).map((l) => (
                      <span key={l} className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-[#E6F0EC] text-[#0A4C36]">
                        {l}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default BookAppointment;
